import React, { useEffect, useState } from 'react';
import { styles } from './ProductServiceModal.css';
import FeaturedListItem from '@jbhi-fi/lanyard-ui/components/FeaturedListItem';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { MarkdownBlock } from '../../MarkdownBlock';
import PriceTag from '@jbhi-fi/lanyard-ui/components/PriceTag';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import Modal from '@jbhi-fi/lanyard-ui/components/Modal';
import ReactDOM from 'react-dom';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ActionButton } from '../../ActionButton';
import { CartActionState } from '../../CartProvider/types';
import { ProductServiceButton } from '../ProductServiceButton/ProductServiceButton';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';

export type ModalWrapperProps = {
  onClose: (event: React.ChangeEvent | React.SyntheticEvent | KeyboardEvent) => void;
  isModalOpen: boolean;
  modalDomElement: Element;
  children: React.ReactNode;
};

export type ProductServiceModalProps = {
  heading: string;
  headingBlock?: React.ReactNode;
  features?: Array<string>;
  disclosureText?: string;
  addToCartDisclosureText?: string;
  priceInc: number;
  shortName: string;
  includedWarranties?: Array<{ heading: string; dateString: string; tooltip?: string | null }>;
  selectedOfferingId?: string;
  preSelectedOfferingId?: string;
  onAdd: (event: React.ChangeEvent | React.SyntheticEvent, offeringId?: string) => Promise<void>;
  onRemove: (event: React.ChangeEvent | React.SyntheticEvent, offeringId?: string) => void;
  buttonsProps?: Array<{
    offeringId: string;
    heading: string;
    price: number;
    elementId: string;
  }>;
  isCondensed?: boolean;
  disclosureTextPositions?: {
    disclosureTextPosition: 'top-right' | 'bottom-left';
    addToCartDisclosureTextPosition: 'top-right' | 'bottom-left';
  };
  isCartReady?: boolean;
};

export const ModalWrapper = ({ onClose, isModalOpen, modalDomElement, children }: ModalWrapperProps) => {
  return ReactDOM.createPortal(
    <div id="modal-container" data-testid={isModalOpen ? 'modal-container-active' : null}>
      <Modal onClose={onClose} show={isModalOpen} size="lg" customClass={styles.modalContainer} zIndex={999999}>
        {children}
      </Modal>
    </div>,
    modalDomElement
  );
};
export const ProductServiceModal = ({
  heading,
  headingBlock,
  features,
  disclosureText,
  addToCartDisclosureText,
  priceInc,
  shortName,
  includedWarranties,
  selectedOfferingId,
  onRemove,
  onAdd,
  buttonsProps,
  isCondensed = false,
  disclosureTextPositions = { disclosureTextPosition: 'top-right', addToCartDisclosureTextPosition: 'top-right' },
  isCartReady = true,
}: ProductServiceModalProps) => {
  const [buttonState, setButtonState] = useState<CartActionState>(CartActionState.Default);
  const [buttonSelection, setButtonSelection] = useState<string | undefined>(
    isCondensed ? selectedOfferingId : undefined
  );
  const [displayPrice, setDisplayPrice] = useState<number>(priceInc);

  const handleClick = async (event: React.ChangeEvent | React.SyntheticEvent) => {
    setButtonState(CartActionState.Updating);
    await onAdd(event, buttonSelection ?? undefined);
  };

  useEffect(() => {
    setButtonState(CartActionState.Default);
  }, [selectedOfferingId]);

  const isDesktop = useBreakpoint('md', 'up');

  return (
    <div className={styles.container({ isCondensed })}>
      <div className={styles.headingContainer({ isCondensed })}>
        <CalloutFont as="h2" className={styles.heading}>
          {heading}
        </CalloutFont>
        {headingBlock ? <div className={styles.headingBlockContainer}>{headingBlock}</div> : null}
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.featureList}>
          {features?.map((feature, i) => (
            <FeaturedListItem
              data-testid={`service-modal-list-item-${i}`}
              key={i}
              heading={
                <MarkdownBlock shouldLinkOpenInNewTab className={styles.featureItem}>
                  {feature}
                </MarkdownBlock>
              }
              icon={<Icon name="CheckboxYellow" width={20} height={20} />}
            />
          ))}
          {includedWarranties ? (
            <div>
              <h5 className={styles.warrantyHeading}>Warranties included with your product</h5>
              <p className={styles.warrantySubHeading}>
                Warranties start from delivery or collection of your product. Dates below are a guide.
              </p>
              <ul className={styles.warrantyList}>
                {includedWarranties.map((warranty, i) => (
                  <li key={i} className={styles.featureItem}>
                    <p className={styles.warrantyText}>
                      <b>{warranty.heading}</b>
                      <br></br>
                      {warranty.dateString}
                      {warranty.tooltip ? (
                        <span className={styles.tooltip}>
                          <br />
                          {warranty.tooltip}
                        </span>
                      ) : null}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {disclosureText && disclosureTextPositions?.disclosureTextPosition === 'bottom-left' ? (
            <MarkdownBlock shouldLinkOpenInNewTab className={styles.disclosureText({ hasBottomMargin: false })}>
              {disclosureText}
            </MarkdownBlock>
          ) : null}
          {addToCartDisclosureText && disclosureTextPositions?.addToCartDisclosureTextPosition === 'bottom-left' ? (
            <MarkdownBlock shouldLinkOpenInNewTab className={styles.disclosureText({ hasBottomMargin: false })}>
              {addToCartDisclosureText}
            </MarkdownBlock>
          ) : null}
        </div>
        <div className={styles.infoBlock}>
          {disclosureText && disclosureTextPositions?.disclosureTextPosition === 'top-right' ? (
            <MarkdownBlock
              shouldLinkOpenInNewTab
              className={styles.disclosureText({
                hasBottomMargin: disclosureTextPositions.addToCartDisclosureTextPosition === 'top-right',
              })}
            >
              {disclosureText}
            </MarkdownBlock>
          ) : null}
          {addToCartDisclosureText && disclosureTextPositions?.addToCartDisclosureTextPosition === 'top-right' ? (
            <MarkdownBlock shouldLinkOpenInNewTab className={styles.disclosureText({ hasBottomMargin: false })}>
              {addToCartDisclosureText}
            </MarkdownBlock>
          ) : null}
          {buttonsProps ? (
            <div className={styles.buttonContainer}>
              <p className={styles.buttonHeading}>Choose duration</p>
              <div className={styles.buttonBlock}>
                {!isCartReady ? (
                  <Icon name="Loading" width={40} height={40} className={styles.buttonLoading} />
                ) : (
                  buttonsProps.map(buttonProps => {
                    return (
                      <ProductServiceButton
                        key={buttonProps.offeringId}
                        shouldHighlightSelection
                        onButtonClick={() => {
                          setButtonSelection(
                            buttonSelection === buttonProps.offeringId ? undefined : buttonProps.offeringId
                          );
                          setDisplayPrice(Number(buttonProps.price));
                        }}
                        selected={buttonSelection === buttonProps.offeringId}
                        {...buttonProps}
                      />
                    );
                  })
                )}
              </div>
            </div>
          ) : null}
          <div className={styles.cta}>
            <PriceTag
              data-testid="service-modal-price-tag"
              actual={displayPrice}
              className={styles.priceBlock}
              variant={isDesktop ? 'default' : 'small'}
            />
            <div className={styles.button}>
              {(isCondensed && selectedOfferingId && selectedOfferingId === buttonSelection) ||
              (!isCondensed && selectedOfferingId) ? (
                <Button
                  data-testid="service-modal-remove-button"
                  type="linkWithIcon"
                  icon={<Icon name="Delete" />}
                  iconPosition="left"
                  loading={!isCartReady}
                  label={`Remove ${shortName}`}
                  onClick={e => onRemove(e, buttonSelection ?? undefined)}
                />
              ) : (
                <ActionButton
                  dataTestId="service-modal-add-button"
                  label={`Add ${shortName}`}
                  isLoading={!isCartReady}
                  onClick={handleClick}
                  state={buttonState}
                  actionFinishedLabel="Added!"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
